import { useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isFirebaseConfigured, signOutFirebase } from "@/lib/firebase";
import { Preferences } from "@capacitor/preferences";

/**
 * AdminAuthGuard — gates /admin/* routes behind a verified admin JWT.
 *
 * Accepts EITHER:
 *   - ?magic=xxx URL param  (Donna-generated magic link — highest priority)
 *   - localStorage.admin_token  (set by the Google Sign-In flow on /admin/login)
 *   - the existing AuthContext user with role:"admin"  (legacy email/password
 *     login still works for backwards compatibility)
 *
 * On first mount we additionally call /api/admin/stats with the token to
 * verify it server-side; a 401/403 boots the user back to /admin/login.
 */
export function AdminAuthGuard({ children }) {
  const { user, loading } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [ok, setOk] = useState(false);
  const [magicError, setMagicError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const magicApprovedRef = useRef(false);

  const magicToken = searchParams.get("magic");
  const adminToken = typeof window !== "undefined" ? localStorage.getItem("admin_token") : null;
  const ctxIsAdmin = !!user && user.role === "admin";

  useEffect(() => {
    let cancelled = false;
    async function verify() {
      if (loading) return;

      // If magic link was already approved this mount, skip re-verification.
      // This prevents the re-render (caused by removing ?magic= from URL)
      // from triggering a stats check with a magic-link JWT that the stats
      // endpoint doesn't recognize.
      if (magicApprovedRef.current) {
        setOk(true);
        setVerifying(false);
        return;
      }

      // === MAGIC LINK FLOW ===
      // If URL has ?magic=xxx, verify it with the backend first
      if (magicToken) {
        try {
          const res = await api.post("/admin/magic-link/verify", { token: magicToken });
          if (res.data?.valid && res.data?.admin_token) {
            // Store the session token and clear the magic param from URL
            localStorage.setItem("admin_token", res.data.admin_token);
            localStorage.setItem("auth_token", res.data.admin_token);
            // Remove ?magic= from URL without reload
            searchParams.delete("magic");
            setSearchParams(searchParams, { replace: true });
            if (!cancelled) {
              magicApprovedRef.current = true;
              setOk(true);
              setVerifying(false);
            }
            return;
          } else {
            // Token invalid or expired
            if (!cancelled) {
              setMagicError("Link expired or invalid. Please request a new one from Donna.");
              setVerifying(false);
            }
            return;
          }
        } catch (e) {
          if (!cancelled) {
            setMagicError("Link expired or invalid. Please request a new one from Donna.");
            setVerifying(false);
          }
          return;
        }
      }

      // === EXISTING AUTH FLOWS ===
      // If neither signal is present, no verification needed — redirect.
      if (!adminToken && !ctxIsAdmin) {
        if (!cancelled) {
          setOk(false);
          setVerifying(false);
        }
        return;
      }
      try {
        // The interceptor in lib/api.js attaches localStorage.auth_token. Our
        // admin Google login mirrors the JWT into both keys, so /admin/stats
        // will be authenticated whichever path the user came from.
        await api.get("/admin/stats");
        if (!cancelled) {
          setOk(true);
          setVerifying(false);
        }
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          // Stale or unauthorized token — clear and redirect. BUT: if the user
          // is currently impersonating another user (driver/shop view), the
          // 403 is expected (impersonation JWT is non-admin). We must NOT
          // wipe admin_token in that case — it's the *only* way to return.
          const isImpersonating = !!user && !!user.impersonator_id;
          if (!isImpersonating) {
            try {
              localStorage.removeItem("admin_token");
            } catch { /* ignore */ }
          }
        }
        if (!cancelled) {
          setOk(false);
          setVerifying(false);
        }
      }
    }
    verify();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, ctxIsAdmin, loading, magicToken]);

  // Magic link error state
  if (magicError) {
    return (
      <div
        className="min-h-screen bg-[#09090B] flex flex-col items-center justify-center text-center gap-4 px-6"
        data-testid="magic-link-error"
      >
        <div className="text-red-400 text-lg font-medium">{magicError}</div>
        <p className="text-zinc-500 text-sm">Ask Donna to generate a fresh link.</p>
      </div>
    );
  }

  if (loading || verifying) {
    return (
      <div
        className="min-h-screen bg-[#09090B] flex items-center justify-center text-zinc-500 label-eyebrow"
        data-testid="admin-guard-loading"
      >
        VERIFYING ADMIN…
      </div>
    );
  }
  if (!ok) {
    // When Google Sign-In isn't configured yet, send users straight to the
    // standard email/password login. Once Firebase Web SDK keys are populated
    // in frontend/.env, this falls through to the dedicated /admin/login page
    // with the Google button.
    const target = isFirebaseConfigured() ? "/admin/login" : "/login";
    return <Navigate to={target} replace />;
  }
  return children;
}

/**
 * adminSignOut — clears both token storage keys + Firebase session, then does
 * a HARD page reload to /login. We deliberately bypass react-router's
 * `navigate()` here because the in-memory AuthContext still holds the admin
 * user object, and Login.jsx auto-redirects logged-in admins back to /admin
 * — creating an infinite loop. `window.location.href` unmounts the whole
 * React tree so AuthContext re-initialises from the now-empty localStorage
 * and the user lands on /login as a clean unauthenticated session.
 *
 * The `_navigate` argument is accepted but ignored, kept for API compat with
 * older callers.
 */
export async function adminSignOut(_navigate) {
  // Call backend logout FIRST while token is still present so the server can invalidate the session.
  try { await api.post("/auth/logout"); } catch { /* ignore */ }
  try { localStorage.removeItem("admin_token"); } catch { /* ignore */ }
  try { localStorage.removeItem("auth_token"); } catch { /* ignore */ }
  try { localStorage.removeItem("demo_mode"); } catch { /* ignore */ }
  try { await Preferences.remove({ key: "shiftquote_auth_token" }); } catch { /* ignore */ }
  try { await signOutFirebase(); } catch { /* firebase not configured — fine */ }
  if (typeof window !== "undefined") {
    // Hard navigation — drops the in-memory React/AuthContext state so the
    // /login page mounts with `user: null` and does not auto-redirect.
    window.location.href = "/login";
  }
}
